import { useState, useCallback } from 'react';
import { CardNumberElement, useStripe, useElements } from '@stripe/react-stripe-js';
import trimDeep from '@hero/hero-utils/trimDeep';
import { findState } from './useAdditionalFields';
export const useStripeFields = () => {
    const stripe = useStripe();
    const elements = useElements();
    const [cardNumberError, updateCardNumberError] = useState('');
    const [cardExpError, updateCardExpError] = useState('');
    const [cardCvcError, updateCardCvcError] = useState('');
    const [stripeError, setStripeError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const handleCardNumberChange = useCallback((e) => updateCardNumberError(e.error?.message || ''), []);
    const handleCardExpChange = useCallback((e) => updateCardExpError(e.error?.message || ''), []);
    const handleCardCvcChange = useCallback((e) => updateCardCvcError(e.error?.message || ''), []);
    const handleSubmit = useCallback((additionalValues, validateAdditionalValues, onSubmit) => async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setStripeError(''); // Clear previous Stripe errors
        if (!stripe || !elements) {
            setIsLoading(false);
            setStripeError('Stripe is not properly initialized. Please try again.');
            return;
        }
        const hasErrors = validateAdditionalValues();
        if (hasErrors) {
            setIsLoading(false);
            return;
        }
        const additionalValuesTrimmed = trimDeep(additionalValues);
        const { name_on_card: name, address1: address_line1, address2: address_line2, city: address_city, province: address_state, zip_code: address_zip } = additionalValuesTrimmed;
        const cardElement = elements.getElement(CardNumberElement);
        try {
            const { token, error } = await stripe.createToken(cardElement, {
                name,
                address_line1,
                address_line2,
                address_city,
                address_state,
                address_zip,
                address_country: 'US'
            });
            if (error) {
                console.error('Stripe token creation error:', error);
                setStripeError(error.message ||
                    'Failed to process payment information. Please check your card details and try again.');
                setIsLoading(false);
                return;
            }
            if (!token?.id) {
                setStripeError('Failed to generate payment token. Please try again.');
                setIsLoading(false);
                return;
            }
            const { province, ...rest } = additionalValuesTrimmed;
            const state = findState(province);
            if (!state) {
                setStripeError('Invalid state/province selected. Please select a valid state.');
                setIsLoading(false);
                return;
            }
            onSubmit({ stripe_token: token.id, province: state.code, ...rest });
        }
        catch (stripeApiError) {
            console.error('Unexpected Stripe API error:', stripeApiError);
            setStripeError('An unexpected error occurred while processing your payment. Please try again.');
        }
        finally {
            setIsLoading(false);
        }
    }, [stripe, elements]);
    return {
        cardNumberError,
        cardExpError,
        cardCvcError,
        stripeError,
        handleCardNumberChange,
        handleCardExpChange,
        handleCardCvcChange,
        handleSubmit,
        isLoading
    };
};
export default useStripeFields;
